import * as React from "react";
import Dialog from "@mui/material/Dialog";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PlaceIcon from "@mui/icons-material/Place";
import { Address, LocalAddress, ResponseObject } from "../../classes/main";
import { ApiService } from "@/app/services/AppService";
import { getToken } from "@/app/services/HolderService";
import CloseIcon from "@mui/icons-material/Close";
import { AddressInputDialog } from "./AddressInputDialog";
import MoreIcon from "@mui/icons-material/MoreVert";
import {
  PAYMENT_MINT,
  PAYMENT_MINT_DARK,
  paymentGradient,
  paymentPrimaryButtonSx,
  cartMobileSurfaceSx,
} from "../cart/payment/paymentUi";

const apiService = new ApiService();

interface AddressCartProps {
  open?: any;
  handleCloseAddressDialog?: any;
  handleOpenProductConfirmDialog?: any;
  isAddressFromProductCart?: any;
  addressForSave?: any;
}

const addressCardSx = {
  ...cartMobileSurfaceSx,
  minHeight: "9.5rem",
  position: "relative" as const,
  border: `1px solid ${alpha(PAYMENT_MINT, 0.14)}`,
  boxShadow: `0 6px 22px ${alpha(PAYMENT_MINT_DARK, 0.06)}`,
  transition: "box-shadow 0.2s ease, border-color 0.2s ease",
  "&:hover": {
    borderColor: alpha(PAYMENT_MINT, 0.28),
    boxShadow: `0 10px 28px ${alpha(PAYMENT_MINT_DARK, 0.1)}`,
  },
};

const AddressCart: React.FC<AddressCartProps> = ({
  open,
  handleCloseAddressDialog,
  handleOpenProductConfirmDialog,
  isAddressFromProductCart,
  addressForSave,
}) => {
  const [addresses, setAddresses] = React.useState<LocalAddress[]>([]);
  const [addressOpen, setAddressOpen] = React.useState(false);
  const [alreadyLogin, setAlreadyLogin] = React.useState(false);
  const [updateAddress, setUpdateAddress] = React.useState(false);
  const [updateAddressFlag, setUpdateAddressFlag] = React.useState(false);
  const [updateAddressData, setUpdateAddressData] =
    React.useState(addressForSave);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedAddress, setSelectedAddress] = React.useState<any>(null);
  const openDropDown = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>, address: any) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedAddress(address);
  };

  const handleCloseAddressDropdown = () => {
    setAnchorEl(null);
  };

  const handleClickOpen = () => {
    setAddressOpen(true);
    setUpdateAddressData((prevState: any) => ({
      ...prevState,
      ...addressForSave,
      building: "",
      locality: "",
      formattedAddress: "",
    }));
  };

  const handleClose = () => {
    setAddressOpen(false);
    setUpdateAddressFlag(false);
  };

  const handleUpdateAddress = async (selectedAddress: any) => {
    setUpdateAddressData(selectedAddress);
    handleCloseAddressDropdown();
    setAddressOpen(true);
    setUpdateAddressFlag(true);
  };

  const handleDeleteAddress = async (selectedAddress: any) => {
    try {
      const responseObject: ResponseObject =
        await apiService.delete<ResponseObject>(
          apiService.deleteCustomerAddresses +
            "?addressId=" +
            selectedAddress?.id,
          getToken()!
        );
    } catch (error) {
      console.log("Error:", error);
    }
    handleCloseAddressDropdown();
    setUpdateAddress(true);
  };

  function selectAddress(address: any) {
    if (isAddressFromProductCart) {
      handleOpenProductConfirmDialog(address);
    }
  }

  async function getAddresses() {
    const token = getToken();
    if (!token) {
      console.log("Token not found.");
      return;
    }
    const addressRaw = localStorage.getItem("address");
    if (!addressRaw) {
      console.log("No address found in localStorage.");
      return;
    }
    const address: Address = JSON.parse(atob(addressRaw));
    try {
      const responseObject = await apiService.get<ResponseObject>(
        apiService.customerAddresses,
        getToken()!
      );
      if (responseObject?.status == "success") {
        const fetchedAddresses = responseObject.object;
        setAddresses(fetchedAddresses);
      }
    } catch (error) {
      console.error("Failed to fetch addresses:", error);
    }
  }

  React.useEffect(() => {
    getAddresses();
    setUpdateAddress(false);
  }, [open, updateAddress]);

  React.useEffect(() => {
    const value = localStorage.getItem("isAlreadyLogin");
    if (value && value == "true") {
      setAlreadyLogin(true);
    }
  }, [open]);

  return (
    <React.Fragment>
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        onClose={handleCloseAddressDialog}
        PaperProps={{
          sx: {
            borderRadius: { xs: 0, sm: "20px" },
            overflow: "hidden",
            border: { xs: "none", sm: `1px solid ${alpha(PAYMENT_MINT, 0.12)}` },
            boxShadow: {
              xs: "none",
              sm: `0 24px 48px ${alpha(PAYMENT_MINT_DARK, 0.12)}`,
            },
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: "100%", sm: "min(92vh, 720px)" },
            display: "flex",
            flexDirection: "column",
          },
        }}
        sx={{
          "& .MuiBackdrop-root": {
            backgroundColor: "rgba(15, 23, 17, 0.45)",
          },
          "@media (max-width: 600px)": {
            "& .MuiDialog-paper": {
              width: "100%",
              height: "100%",
              maxHeight: "100%",
              margin: 0,
            },
          },
        }}
      >
        <Box
          sx={{
            background: paymentGradient,
            px: { xs: 2, sm: 2.5 },
            py: { xs: 2, sm: 2.25 },
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                component="h2"
                sx={{
                  color: "#fff",
                  fontWeight: 800,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.25,
                }}
              >
                Service location
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: alpha("#fff", 0.88),
                  display: "block",
                  mt: 0.5,
                  lineHeight: 1.4,
                }}
              >
                Choose where you want the service, or add a new address.
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseAddressDialog}
              aria-label="Close"
              sx={{
                color: "#fff",
                bgcolor: alpha("#fff", 0.14),
                "&:hover": { bgcolor: alpha("#fff", 0.24) },
                flexShrink: 0,
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            px: { xs: 2, sm: 2.5 },
            py: 2,
            bgcolor: "#fafcfb",
            scrollbarWidth: "thin",
            "&::-webkit-scrollbar": { width: 6 },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 999,
              background: alpha(PAYMENT_MINT, 0.28),
            },
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={handleClickOpen}
            startIcon={<AddCircleOutlineIcon sx={{ color: PAYMENT_MINT }} />}
            sx={{
              justifyContent: "flex-start",
              py: 1.25,
              px: 2,
              borderRadius: "14px",
              textTransform: "none",
              fontWeight: 700,
              color: PAYMENT_MINT_DARK,
              borderColor: alpha(PAYMENT_MINT, 0.35),
              bgcolor: alpha("#fff", 0.95),
              "&:hover": {
                borderColor: PAYMENT_MINT,
                bgcolor: "#fff",
              },
            }}
          >
            Add new address
          </Button>

          <Grid
            container
            spacing={2}
            sx={{ mt: 0.5 }}
          >
            {alreadyLogin
              ? addresses
                ? addresses?.map((address, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      key={"Address" + index}
                    >
                      <Box sx={addressCardSx}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "flex-start",
                              alignItems: "flex-start",
                              gap: 1.25,
                              minWidth: 0,
                              pr: 4,
                            }}
                          >
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: "12px",
                                bgcolor: alpha(PAYMENT_MINT, 0.1),
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              <PlaceIcon
                                sx={{ fontSize: 20, color: PAYMENT_MINT }}
                              />
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#0b1710",
                                lineHeight: 1.5,
                                wordBreak: "break-word",
                              }}
                            >
                              {address?.formattedAddress ?? ""}
                              {address?.building ? (
                                <>
                                  <br />
                                  {address?.building}
                                </>
                              ) : null}
                              {address?.locality ? (
                                <>
                                  <br />
                                  {address?.locality}
                                </>
                              ) : null}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            aria-label="Address actions"
                            onClick={(event) => handleClick(event, address)}
                            sx={{
                              position: "absolute",
                              top: 8,
                              right: 4,
                              color: PAYMENT_MINT_DARK,
                              "&:hover": { bgcolor: alpha(PAYMENT_MINT, 0.08) },
                            }}
                          >
                            <MoreIcon fontSize="small" />
                          </IconButton>
                        </Box>
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 12,
                            right: 12,
                          }}
                        >
                          <Button
                            variant="contained"
                            onClick={() => selectAddress(address)}
                            sx={{
                              ...paymentPrimaryButtonSx,
                              py: 0.65,
                              px: 2,
                              minHeight: 38,
                              fontSize: "0.875rem",
                              textTransform: "none",
                              fontWeight: 700,
                            }}
                          >
                            Select
                          </Button>
                        </Box>
                        <Menu
                          anchorEl={anchorEl}
                          id="address-actions-menu"
                          open={openDropDown}
                          onClose={handleCloseAddressDropdown}
                          slotProps={{
                            paper: {
                              elevation: 0,
                              sx: {
                                mt: 1,
                                borderRadius: "12px",
                                border: `1px solid ${alpha(PAYMENT_MINT, 0.12)}`,
                                boxShadow: `0 12px 32px ${alpha(PAYMENT_MINT_DARK, 0.12)}`,
                                minWidth: 160,
                              },
                            },
                          }}
                          transformOrigin={{
                            horizontal: "right",
                            vertical: "top",
                          }}
                          anchorOrigin={{
                            horizontal: "right",
                            vertical: "bottom",
                          }}
                        >
                          <MenuItem
                            onClick={() => handleUpdateAddress(selectedAddress)}
                            sx={{ fontWeight: 600, fontSize: "0.875rem" }}
                          >
                            Edit address
                          </MenuItem>
                          <MenuItem
                            onClick={() => handleDeleteAddress(selectedAddress)}
                            sx={{ color: "error.main", fontWeight: 600, fontSize: "0.875rem" }}
                          >
                            Delete
                          </MenuItem>
                        </Menu>
                      </Box>
                    </Grid>
                  ))
                : Array.from({ length: 4 }).map((_, i) => (
                    <Grid item xs={12} sm={6} key={i}>
                      <Skeleton
                        variant="rounded"
                        sx={{
                          width: "100%",
                          height: 160,
                          borderRadius: "16px",
                        }}
                      />
                    </Grid>
                  ))
              : null}
          </Grid>
        </Box>
      </Dialog>
      <AddressInputDialog
        addressOpen={addressOpen}
        handleClose={handleClose}
        addressForSave={updateAddressData}
        updateAddressList={() => setUpdateAddress(true)}
        updateAddressFlag={updateAddressFlag}
      />
    </React.Fragment>
  );
};

export default AddressCart;
